// Pure builders for the BigHarvest capture surface (P1b) — tested, so the
// page's mutation/query glue stays plain I/O (the harvestForms precedent).
import type { ClockLocation } from '@/lib/clockLocation'
import { newUuid } from '@/lib/uuid'

export interface RosterWorker {
  worker_number: string
  name:          string
}

/** P4.6: the two activities a HARVEST clock event can book to. GENERAL is
 *  parent-team morning work (D6); the DB rejects anything else. */
export type HarvestWorkcode = 'HARVEST' | 'GENERAL'

/** One clock scan for record_span_clock_in — client-minted id = the
 *  idempotency key that makes offline replays safe. P4.6: statusCode 1 = OUT
 *  and workcode 'GENERAL' book the event; both default to the pre-P4.6 stamp
 *  (IN / HARVEST) so every queued payload replays unchanged. */
export function buildClockScan(
  workerNumber: string,
  method: 'BADGE' | 'MANUAL',
  scannedAt: string,
  loc: ClockLocation | null,
  opts?: { statusCode?: 0 | 1; workcode?: HarvestWorkcode },
): Record<string, unknown> {
  return {
    client_scan_id: newUuid(),
    worker_number:  workerNumber,
    scanned_at:     scannedAt,
    method,
    status_code:    opts?.statusCode ?? 0,
    workcode:       opts?.workcode ?? 'HARVEST',
    lat:                  loc?.lat ?? null,
    lng:                  loc?.lng ?? null,
    accuracy_m:           loc?.accuracy_m ?? null,
    location_captured_at: loc?.captured_at ?? null,
  }
}

/** A tally row for save_pick_tallies. BADGE rows carry badge_code — the
 *  server resolves capture_badge_id itself (forgery guard). */
export function buildTallyRow(input: {
  workerNumber: string
  units:        number
  unitType:     string
  teamId:       string
  via:          'BADGE' | 'MANUAL'
  badgeCode?:   string | null
}): Record<string, unknown> {
  return {
    pick_tally_id:  newUuid(),
    worker_number:  input.workerNumber,
    units:          input.units,
    unit_type:      input.unitType,
    team_id:        input.teamId,
    identified_via: input.via,
    ...(input.via === 'BADGE' ? { badge_code: input.badgeCode ?? null } : {}),
  }
}

interface LiveTally { worker_number: string; units: number }
interface PendingTallyEntry { pick_id: string; tallies: Array<Record<string, unknown>> }

/** Per-worker unit totals for TODAY's pick: live rows ∪ still-queued rows.
 *  Queued entries are keyed by pick_id; a tally that already landed live is
 *  deduped by pick_tally_id (client-minted, stable across retries). */
export function mergeTallyTotals(
  live: Array<LiveTally & { pick_tally_id: string }>,
  pending: PendingTallyEntry[],
  pickId: string,
): Map<string, number> {
  const seen = new Set(live.map(t => t.pick_tally_id))
  const totals = new Map<string, number>()
  const add = (worker: string, units: number) => {
    if (!Number.isFinite(units) || units <= 0) return
    totals.set(worker, (totals.get(worker) ?? 0) + units)
  }
  for (const t of live) add(t.worker_number, Number(t.units))
  for (const entry of pending) {
    if (entry.pick_id !== pickId) continue
    for (const t of entry.tallies) {
      const id = String(t.pick_tally_id ?? '')
      if (id && seen.has(id)) continue
      add(String(t.worker_number ?? ''), Number(t.units))
    }
  }
  totals.delete('')
  return totals
}

/** Units input → validated positive number (fractions allowed: half-bins are
 *  real at day end). */
export function parseUnits(raw: string): number | null {
  const n = Number(raw.replace(',', '.'))
  if (!Number.isFinite(n) || n <= 0 || n > 999) return null
  return Math.round(n * 100) / 100
}

/** DN numbers are hand-typed on a phone in an orchard. Trim and collapse
 *  internal whitespace so "DN 4471" and "DN  4471 " are one document; case is
 *  left ALONE (the DB matches case-insensitively and keeps what was typed
 *  first, so the supervisor's own spelling survives on the printed report). */
export function normaliseDnNumber(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ')
}

/** Payload for record_harvest_load. load_id is client-minted — it is the
 *  idempotency key that lets an offline drain replay a lost-response attempt
 *  exactly once, the same contract buildClockScan relies on. */
export function buildLoadPayload(input: {
  siteId:        string
  teamId?:       string | null
  blockId?:      string | null
  destinationId?: string | null
  dnNumber?:     string | null
  dnPhotoUrl?:   string | null
  binsLoaded:    number
  vehicleReg?:   string | null
  driverName?:   string | null
  notes?:        string | null
  loadedAt?:     string
  loc:           ClockLocation | null
}): Record<string, unknown> {
  const dn = input.dnNumber ? normaliseDnNumber(input.dnNumber) : ''
  const loadedAt = input.loadedAt ?? new Date().toISOString()
  return {
    load_id:        newUuid(),
    site_id:        input.siteId,
    team_id:        input.teamId ?? null,
    block_id:       input.blockId ?? null,
    destination_id: input.destinationId ?? null,
    dn_number:      dn || null,
    dn_photo_url:   input.dnPhotoUrl ?? null,
    bins_loaded:    String(input.binsLoaded),
    vehicle_reg:    input.vehicleReg ?? null,
    driver_name:    input.driverName ?? null,
    notes:          input.notes ?? null,
    loaded_at:      loadedAt,
    // Local calendar day, not the UTC slice — a load at 01:00 SAST belongs to
    // that day's ticket, and a raw ISO slice would book it to the day before
    // (the sastYMD lesson from the P2 day sheets).
    load_date:      sastYMD(loadedAt),
    lat:            input.loc?.lat ?? null,
    lng:            input.loc?.lng ?? null,
    accuracy_m:     input.loc?.accuracy_m ?? null,
  }
}

/** Local (Africa/Johannesburg) calendar date for an ISO instant. */
function sastYMD(iso: string): string {
  return new Date(iso).toLocaleDateString('en-CA', { timeZone: 'Africa/Johannesburg' })
}

export interface LoadOutForm {
  bins:          string
  dn:            string
  destinationId: string
  vehicle:       string
  driver:        string
}

/** Validate + shape a Load-out form into the RPC payload. Returns null when
 *  the bin count is unusable, so the page can show one message and stop —
 *  keeping the page's save path plain I/O (the harvestForms precedent). */
export function loadFormToPayload(
  form: LoadOutForm,
  ctx: { siteId: string; teamId: string; blockId?: string | null; loc: ClockLocation | null },
): Record<string, unknown> | null {
  const bins = parseUnits(form.bins)
  if (bins === null) return null
  return buildLoadPayload({
    siteId:        ctx.siteId,
    teamId:        ctx.teamId,
    // The block the screen is working in — GPS-suggested, supervisor-confirmed.
    blockId:       ctx.blockId ?? null,
    destinationId: form.destinationId || null,
    dnNumber:      form.dn || null,
    binsLoaded:    bins,
    vehicleReg:    form.vehicle || null,
    driverName:    form.driver || null,
    loc:           ctx.loc,
  })
}

/** General-work session, persisted as a flat string ('' = none).
 *
 *  P4.7 added the SITE and the TASK, so the encoding is versioned:
 *    `v2|${sessionId}|${teamId}|${siteId}|${taskId}|${YYYY-MM-DD}|${csv}`
 *  with an empty siteId meaning "not at a client site" (yard / travel).
 *
 *  The P4.6 shape was `${teamId}|${date}|${csv}` — unversioned, and its first
 *  field is a UUID exactly like v2's second. A phone upgrading mid-morning
 *  therefore holds a string that PARSES under either reader and means
 *  different things. Anything not starting `v2|` is refused outright rather
 *  than coerced: a cleared card costs the supervisor one re-tick, while a
 *  mis-parsed one would clock the wrong team out. */
export interface GeneralSession {
  sessionId: string
  teamId:    string
  siteId:    string | null
  taskId:    string
  date:      string
  workers:   string[]
}

export function parseGeneralSession(raw: string): GeneralSession | null {
  if (!raw || !raw.startsWith('v2|')) return null
  const [, sessionId, teamId, siteId, taskId, date, csv] = raw.split('|')
  if (!sessionId || !teamId || !taskId || !date) return null
  return {
    sessionId, teamId, taskId, date,
    siteId:  siteId || null,
    workers: csv ? csv.split(',') : [],
  }
}

export function encodeGeneralSession(s: GeneralSession): string {
  return ['v2', s.sessionId, s.teamId, s.siteId ?? '', s.taskId, s.date, s.workers.join(',')].join('|')
}

/** The RPC payload for record_general_work_session — one object carrying the
 *  session, its crew and the clock scans, so a general-work morning can never
 *  half-land as hours with no task or a task with nobody on it. Pure, because
 *  the offline drain replays exactly this and needs the same bytes. */
export function buildGeneralSessionPayload(args: {
  mode:      'START' | 'END'
  session:   GeneralSession
  scans:     Record<string, unknown>[]
  startedAt?: string
  loc?:      ClockLocation | null
}): Record<string, unknown> {
  const { mode, session, scans, startedAt, loc } = args
  // The crew rows pair each worker with the client_scan_id of THIS scan, which
  // is what makes the join back to clock_scans exact rather than a time-window
  // guess. Built from the scans themselves so the two can never disagree.
  const crew = scans.map(s => ({
    worker_number:  s.worker_number,
    client_scan_id: s.client_scan_id,
  }))
  const base: Record<string, unknown> = {
    session_id: session.sessionId,
    mode,
    crew,
    scans,
  }
  if (mode === 'END') return { ...base, ended_at: startedAt ?? new Date().toISOString() }
  return {
    ...base,
    team_id:    session.teamId,
    task_id:    session.taskId,
    site_id:    session.siteId ?? null,
    work_date:  session.date,
    started_at: startedAt ?? new Date().toISOString(),
    start_lat:  loc?.lat ?? null,
    start_lng:  loc?.lng ?? null,
  }
}

/** P46-04: did `record_span_clock_in` actually write anything? It RETURNS
 *  integer (the inserted count) and skips a stray OUT silently, so a batch
 *  that wrote nothing is otherwise indistinguishable from one that wrote
 *  everything. Lives here — pure and tested — because both the live path and
 *  the offline DRAIN need the same answer, and offlineQueue.ts has no test
 *  file of its own.
 *
 *  PostgREST sends a bare scalar for a scalar-returning function; the array
 *  unwrap guards supabase-js's shape ambiguity (the has_worker_pin precedent).
 *  Only an EXPLICIT numeric 0 is a no-op — any other shape reads as 'sent', so
 *  a wrong guess degrades to the old behaviour rather than crying wolf on a
 *  money rail. A PARTIAL insert is 'sent' too: the stray-OUT guard skipping
 *  already-closed workers is it working as designed. */
export function spanClockOutcome(data: unknown): 'sent' | 'noop' {
  const inserted = Array.isArray(data) ? data[0] : data
  return typeof inserted === 'number' && inserted === 0 ? 'noop' : 'sent'
}

/** P46-04: what a supervisor is told about a general-work session left open on
 *  a PAST day. Pure + tested because this copy makes FACTUAL claims — a
 *  deadline date and what the office can still do — and a wrong one sends the
 *  crew's hours nowhere.
 *
 *  Within 7 days the office can still write the missing clock-out (attest).
 *  Past it, the clock EVENT is unrecoverable — both attest RPCs hard-refuse
 *  >7 days — which is what keeps the day reading "still clocked in" and what
 *  the hours file exports. It deliberately does NOT say "nothing can be done":
 *  the office can still correct the recorded hours on the labour record. */
export function staleSessionNotice(input: {
  sessionDate: string
  today:       string
  workerCount: number
  /** Injected so this stays pure: (ymd) => display string, (ymd, n) => ymd. */
  fmtDate:     (ymd: string) => string
  addDays:     (ymd: string, days: number) => string
}): { title: string; message: string; attestable: boolean } {
  const { sessionDate, today, workerCount, fmtDate, addDays } = input
  const attestable = sessionDate >= addDays(today, -7)
  const who = `${workerCount} worker${workerCount === 1 ? '' : 's'} ${workerCount === 1 ? 'is' : 'are'}`
  return {
    attestable,
    title: `That session is from ${fmtDate(sessionDate)}`,
    message: attestable
      ? `Clock-outs for a past day can't be written here — this screen only closes today. `
        + `${who} still clocked in on ${fmtDate(sessionDate)}; ask the office to write the real `
        + `end time in Pickers → Attendance, which is possible until ${fmtDate(addDays(sessionDate, 7))}. `
        + `Clearing this card just tidies your screen — the open day stays visible to the office either way.`
      : `Clock-outs for a past day can't be written here, and ${fmtDate(sessionDate)} is now past the `
        + `7-day window for adding a missing clock-out — so that day will keep reading as `
        + `"still clocked in" on the harvest screens. Still tell the office: they can correct the `
        + `recorded hours on the worker's attendance record. Clearing this card just tidies your screen.`,
  }
}

/** P46-04: what the supervisor is told when an OUT batch writes NOTHING.
 *  Shared by BOTH clock-out surfaces (Capture's "Close the day" and General
 *  work's "End") because the fact and its two possible causes are identical —
 *  and because two hand-maintained copies of a safety message drift.
 *  Data, not UI: the pages pass it to alertDialog. */
export const NOTHING_CLOCKED_OUT_ALERT = {
  title: 'Nothing was clocked out',
  message:
    'No clock-outs were recorded. Usually that means they were already closed — '
    + 'starting a pick closes general work automatically. But it can also mean '
    + 'their clock-ins never reached us. Check Day end: if anyone is still shown '
    + 'clocked in, the office can write their times in Pickers → Attendance.',
  tone: 'warning' as const,
}

/** The pick-capture day session (UXH-01), persisted like GeneralSession so an
 *  app restart mid-day restores who is present — without it, `present` lived
 *  in memory only and a reload turned "Close the day" into a silent no-op.
 *  Flat string ('' = none): `${spanId}|${YYYY-MM-DD}|${csv}` where each csv
 *  entry is `worker_number` or `worker_number@B` (badge-verified presence —
 *  the suffix survives the round-trip so a restart can't downgrade BADGE
 *  evidence to MANUAL), then `|S` when that sub-team has STARTED today.
 *
 *  H1 D3 — `started` is a fact separate from presence. The roster ticks the
 *  whole crew present on load, so "has anyone present" was true the moment a
 *  sub-team was chosen, and Site locked before Start pick was ever tapped. A
 *  sub-team is started once its pick for today has landed or queued (or the
 *  screen found one). A line without the flag — every document written before
 *  this — parses as NOT started; an older bundle reading a new line simply
 *  ignores the fourth field.
 *
 *  H1 D6 — a STARTED line may carry a fifth field, the rules that sub-team's
 *  day was started under: `U=<unit>;P=<0|1>;G=<0|1>` (unit, photo required,
 *  GPS required). See CaptureDayRules. Older bundles split on '|' and read
 *  four fields at most, so they ignore it; a line without it parses as
 *  `rules: null`. */
export interface CaptureSession {
  spanId:  string
  date:    string
  workers: Array<{ worker_number: string; via: 'BADGE' | 'MANUAL' }>
  started: boolean
  rules:   CaptureDayRules | null
}

/** H1 D6 — what a sub-team's day was started under, kept with the day on the
 *  phone so a settings read that fails LATER can't change (or lose) the unit
 *  its counts are booked in. Nothing server-side checks a tally's unit, so the
 *  phone is the only place this can be held. */
export interface CaptureDayRules {
  unit:       string
  needPhoto:  boolean
  requireGps: boolean
}

// The unit rides inside a '|'- and '\n'-separated document, so it must be a
// plain code. Same shape harvest_config's CHECK list uses (BIN, CRATE, …).
const UNIT_CODE = /^[A-Z][A-Z0-9_]{0,15}$/

function encodeDayRules(r: CaptureDayRules): string | null {
  if (!UNIT_CODE.test(r.unit)) return null
  return `U=${r.unit};P=${r.needPhoto ? 1 : 0};G=${r.requireGps ? 1 : 0}`
}

function parseDayRules(raw: string | undefined): CaptureDayRules | null {
  if (!raw) return null
  const kv = new Map(raw.split(';').map(part => {
    const i = part.indexOf('=')
    return [part.slice(0, i), part.slice(i + 1)] as const
  }))
  const unit = kv.get('U') ?? ''
  if (!UNIT_CODE.test(unit)) return null
  return { unit, needPhoto: kv.get('P') === '1', requireGps: kv.get('G') === '1' }
}

export function encodeCaptureSession(
  spanId:  string,
  date:    string,
  present: Set<string>,
  via:     Map<string, 'BADGE' | 'MANUAL'>,
  started = false,
  rules: CaptureDayRules | null = null,
): string {
  const csv = [...present].map(n => (via.get(n) === 'BADGE' ? `${n}@B` : n)).join(',')
  // Rules only ever belong to a started day — the fifth field needs the fourth.
  const r = started && rules ? encodeDayRules(rules) : null
  return `${spanId}|${date}|${csv}${started ? '|S' : ''}${r ? `|${r}` : ''}`
}

export function parseCaptureSession(raw: string): CaptureSession | null {
  if (!raw) return null
  const [spanId, date, csv, flag, rules] = raw.split('|')
  if (!spanId || !date) return null
  const workers = (csv ? csv.split(',') : []).filter(Boolean).map(entry => {
    const badge = entry.endsWith('@B')
    return {
      worker_number: badge ? entry.slice(0, -2) : entry,
      via: (badge ? 'BADGE' : 'MANUAL') as 'BADGE' | 'MANUAL',
    }
  }).filter(w => w.worker_number)
  const started = flag === 'S'
  return { spanId, date, workers, started, rules: started ? parseDayRules(rules) : null }
}

/** Restoration payload for the CURRENT sub-team + day, or null when the
 *  stored session is for a different span/date (stale sessions never
 *  restore — they just get overwritten by the next write-through). */
export function restoredCapturePresence(
  raw: string, spanId: string, today: string,
): { present: Set<string>; via: Map<string, 'BADGE' | 'MANUAL'> } | null {
  const s = parseCaptureSession(raw)
  if (!s || !spanId || s.spanId !== spanId || s.date !== today) return null
  return {
    present: new Set(s.workers.map(w => w.worker_number)),
    via: new Map(s.workers.filter(w => w.via === 'BADGE')
      .map(w => [w.worker_number, 'BADGE' as const])),
  }
}

/** Whether the one-shot restore of a persisted day session may run yet
 *  (UXH-01). It waits for BOTH the tenant and the sub-team to resolve — until
 *  then the persisted key still holds its initial '', and hydrating from it
 *  would latch "nothing restored" for the rest of the mount. `already` is the
 *  mount's own latch: restore happens once, never again. */
export function canHydrateCapture(input: {
  tenantId: string | null | undefined
  spanId:   string
  already:  boolean
}): boolean {
  const { tenantId, spanId, already } = input
  if (already) return false
  return !!tenantId && !!spanId
}

/** MULTI-TEAM (Q5, answered by Johan 2026-08-13: one phone runs several
 *  sub-teams). The day's presence is stored per sub-team, so 1A and 1B can
 *  both be open on one device and a restart brings BOTH back.
 *
 *  Wire format is one line per sub-team, newline-separated, each line the
 *  single-session format `${spanId}|${date}|${csv}`. A legacy single-session
 *  string is exactly a one-line document, so existing devices migrate for
 *  free — the supervisor mid-day when this ships keeps their crew. */
export function encodeCaptureSessions(
  sessions: Array<{
    spanId: string; date: string; present: Set<string>; via: Map<string, 'BADGE' | 'MANUAL'>
    started?: boolean
    rules?: CaptureDayRules | null
  }>,
): string {
  return sessions
    .filter(s => s.spanId)
    .map(s => encodeCaptureSession(s.spanId, s.date, s.present, s.via, s.started === true, s.rules ?? null))
    .join('\n')
}

/** One sub-team's line of today's day session, parsed. */
export interface CaptureDaySpan {
  present: Set<string>
  via:     Map<string, 'BADGE' | 'MANUAL'>
  started: boolean
  rules:   CaptureDayRules | null
}

/** Parse the multi-session document → span → presence (+ whether that
 *  sub-team has started today, and the rules it started under), dropping any
 *  line for another day (a session that outlived its date must not resurrect
 *  — nor lock tomorrow's Site, nor lend tomorrow yesterday's unit). */
export function parseCaptureSessions(
  raw: string, today: string,
): Map<string, CaptureDaySpan> {
  const out = new Map<string, CaptureDaySpan>()
  for (const line of (raw ?? '').split('\n')) {
    const s = parseCaptureSession(line.trim())
    if (!s || s.date !== today) continue
    out.set(s.spanId, {
      present: new Set(s.workers.map(w => w.worker_number)),
      via: new Map(s.workers.filter(w => w.via === 'BADGE')
        .map(w => [w.worker_number, 'BADGE' as const])),
      started: s.started,
      rules:   s.rules,
    })
  }
  return out
}

/** What a mount holds for today, each part carrying its day (a phone left
 *  open overnight must not wake up with yesterday's teams still "out"). */
export interface CaptureDayState {
  started: { day: string; spans: Set<string> }
  rules:   { day: string; bySpan: Map<string, CaptureDayRules> }
}

/** UXH-01 + H1 D3/D6 — fold a restored day document into what the mount
 *  already holds for today. Union, never replace: on a warm cache the viewed
 *  team's pick may already have been found before the restore ran. A line
 *  written before D3 carries no flag and restores as not started. For the
 *  rules the STORED copy wins — the settings may have landed first, and they
 *  need not be what Start kept. Anything held for another day is dropped. */
export function mergeRestoredDay(
  restored: Map<string, CaptureDaySpan>,
  today:    string,
  held:     CaptureDayState,
): CaptureDayState {
  const spans = new Set(held.started.day === today ? held.started.spans : [])
  const bySpan = new Map(held.rules.day === today ? held.rules.bySpan : [])
  for (const [sp, v] of restored) {
    if (v.started) spans.add(sp)
    if (v.rules) bySpan.set(sp, v.rules)
  }
  return { started: { day: today, spans }, rules: { day: today, bySpan } }
}

/** H1 D6 — the unit (and photo / GPS rules) Capture may count in right now.
 *
 *  There is NO fallback unit. `default_unit_type ?? 'BIN'` booked every count
 *  in BIN, said "unit: BIN (locked)" and switched the photo and GPS rules off
 *  whenever the settings read was pending or had failed — and nothing
 *  server-side checks a tally's unit.
 *
 *  - The DAY wins for the unit: a sub-team's day keeps the unit it was started
 *    in (or first seen in on this phone), so one pick's counts never mix
 *    units and a later failed read can't take it away.
 *  - The SETTINGS win for the photo and GPS rules while they are loaded — an
 *    office that tightens the photo rule mid-day is obeyed at once, as before —
 *    and the day's copy stands in when they are not, so a failed read never
 *    silently switches a rule off.
 *  - Neither known → not known, and why: still loading, the read failed (or is
 *    paused offline with nothing cached), or it answered with no unit set. */
export type CaptureUnitResolution =
  | { known: true; unit: string; needPhoto: boolean; requireGps: boolean; from: 'day' | 'settings' }
  | { known: false; why: 'loading' | 'failed' | 'not-set' }

export function resolveCaptureUnit(input: {
  /** readState() of the harvest_config query. */
  read: 'usable' | 'unread' | 'loading'
  cfg:  { default_unit_type?: string | null; require_pick_photo?: boolean | null; require_gps?: boolean | null } | null | undefined
  /** The viewed sub-team's stored rules for today, if its day has them. */
  day:  CaptureDayRules | null
}): CaptureUnitResolution {
  const { read, cfg, day } = input
  const settings: CaptureDayRules | null =
    read === 'usable' && cfg && cfg.default_unit_type && UNIT_CODE.test(cfg.default_unit_type)
      ? { unit: cfg.default_unit_type, needPhoto: cfg.require_pick_photo === true, requireGps: cfg.require_gps === true }
      : null
  if (day) {
    const rules = settings ?? day
    return { known: true, unit: day.unit, needPhoto: rules.needPhoto, requireGps: rules.requireGps, from: 'day' }
  }
  if (settings) return { known: true, ...settings, from: 'settings' }
  if (read === 'loading') return { known: false, why: 'loading' }
  if (read === 'unread') return { known: false, why: 'failed' }
  return { known: false, why: 'not-set' }
}

/** Presence always belongs to ONE sub-team. This decides what the selected
 *  sub-team's presence should become — the step whose absence let 1B's crew
 *  stay selected under 1A's name (and get clocked in under it).
 *
 *  Order matters: an existing snapshot for this span wins (that is the
 *  supervisor's own unticks and BADGE evidence, from before they looked at
 *  another team), then the all-present default once the roster has landed.
 *  Returns null when nothing should change — already owned, or still waiting
 *  on the roster, in which case presence stays EMPTY rather than borrowed. */
export function nextPresenceForSpan(input: {
  spanId:      string
  ownerSpan:   string
  snapshot?:   { present: Set<string>; via: Map<string, 'BADGE' | 'MANUAL'> } | null
  roster:      RosterWorker[]
  started:     boolean
}): { present: Set<string>; via: Map<string, 'BADGE' | 'MANUAL'> } | null {
  const { spanId, ownerSpan, snapshot, roster, started } = input
  if (!spanId || ownerSpan === spanId) return null
  if (snapshot) return { present: new Set(snapshot.present), via: new Map(snapshot.via) }
  if (started || roster.length === 0) return null
  return { present: new Set(roster.map(w => w.worker_number)), via: new Map() }
}

/** General work books to the PARENT team; its roster is the union of the
 *  parent's own members and every sub-team's (memberships mostly live at
 *  sub-team level). */
export function generalRoster(
  spans:   Array<{ team_id: string; parent_team_id: string | null }>,
  members: Array<{ team_id: string; worker_number: string }>,
  workers: RosterWorker[],
  teamId:  string,
): RosterWorker[] {
  const ids = new Set([teamId, ...spans.filter(s => s.parent_team_id === teamId).map(s => s.team_id)])
  const nums = new Set(members.filter(m => ids.has(m.team_id)).map(m => m.worker_number))
  return workers.filter(w => nums.has(w.worker_number))
}

/**
 * What a badge scan on the capture screen MEANS, decided in one place.
 *
 * The handler used to make four judgements inline — is the badge known, has
 * the day started, is this picker off-team, what does the operator get told —
 * and each of them matters more than its two lines suggest. `offTeam` in
 * particular is advisory ONLY: the caller must land the count regardless and
 * merely offer the lend (locked decision — they are offline, and a scan held
 * up waiting for an answer is a bin nobody finds until day end).
 */
export type BadgeScanOutcome =
  /** `retryBadges`: the badge LIST failed to load, so the screen offers Retry
   *  (re-read it) beside the message instead of a bare OK. */
  | { kind: 'unknown'; message: string; retryBadges: boolean }
  | {
      kind: 'present' | 'units'
      worker: RosterWorker
      badgeCode: string
      /** The picker to warn about, or null. Note it rides ALONGSIDE a real
       *  outcome and can never replace one: making the off-team case a
       *  variant would make "refuse the scan" expressible, and the locked
       *  decision is that it never is. */
      offTeamNotice: RosterWorker | null
    }

export function badgeScanOutcome(args: {
  hit:      { badge_code: string; worker_number: string } | null
  anyBadgesLoaded: boolean
  /** The badge read ERRORED (the query's own error state). A paused offline
   *  read is not an error — that phone genuinely has nothing synced. */
  badgeListFailed: boolean
  nameOf:   (workerNumber: string) => string
  started:  boolean
  isOffTeam: (workerNumber: string) => boolean
}): BadgeScanOutcome {
  if (!args.hit) {
    // H1 D11: a FAILED read is a third fault with a third fix. Told "open this
    // screen once while online", a supervisor who IS online and on the screen
    // has nothing to do — the list needs re-reading, and the bin can still be
    // counted by name meanwhile. Checked first: with the read failed, neither
    // "never synced" nor "not recognised" is known to be true.
    if (args.badgeListFailed) {
      return {
        kind: 'unknown',
        retryBadges: true,
        message: "Couldn't load the latest badge list, so this badge can't be matched. Tap Retry, then scan again"
          + (args.started
            ? " — or tap the picker's name to add the count by hand."
            : " — or tick the picker's name to mark them present."),
      }
    }
    return {
      kind: 'unknown',
      retryBadges: false,
      // Two different faults with two different fixes: nothing synced yet is
      // the supervisor's morning mistake, an unrecognised code is the office's
      // reprint. Collapsing them sends people to the wrong screen.
      message: !args.anyBadgesLoaded
        ? 'No badges synced yet — open this screen once while online.'
        : 'Badge not recognised. Reprint it from Pickers, or add the count by tapping the picker.',
    }
  }
  const worker: RosterWorker =
    { worker_number: args.hit.worker_number, name: args.nameOf(args.hit.worker_number) }
  return {
    kind:      args.started ? 'units' : 'present',
    worker,
    badgeCode: args.hit.badge_code,
    offTeamNotice: args.isOffTeam(worker.worker_number) ? worker : null,
  }
}
